import type { Breakpoint } from './index';
import type { ReactElement, PropsWithChildren } from 'react';

import styled, { css } from 'styled-components';

import { media } from './media';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';

type JustifyType =
  | 'center'
  | 'end'
  | 'flex-end'
  | 'flex-start'
  | 'inherit'
  | 'initial'
  | 'left'
  | 'normal'
  | 'revert'
  | 'right'
  | 'space-around'
  | 'space-between'
  | 'space-evenly'
  | 'start'
  | 'stretch'
  | 'unset';

type AlignType =
  | 'baseline'
  | 'center'
  | 'end'
  | 'flex-end'
  | 'flex-start'
  | 'inherit'
  | 'initial'
  | 'normal'
  | 'revert'
  | 'self-end'
  | 'self-start'
  | 'start'
  | 'stretch'
  | 'unset';

type DirectionType = 'column' | 'row';

type ColumnRangeType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

interface ContainerProps {
  className?: string;
}

export interface RowProps {
  justify?: JustifyType;
  align?: AlignType;
  direction?: DirectionType;
  className?: string;
}

interface StyledRowProps {
  $justify?: JustifyType;
  $align?: AlignType;
  $direction?: DirectionType;
}

export interface ColumnProps {
  xs?: ColumnRangeType;
  sm?: ColumnRangeType;
  md?: ColumnRangeType;
  lg?: ColumnRangeType;
  xl?: ColumnRangeType;
  className?: string;
  displayType?: string;
}

interface StyledColumnProps {
  $xs?: ColumnRangeType;
  $sm?: ColumnRangeType;
  $md?: ColumnRangeType;
  $lg?: ColumnRangeType;
  $xl?: ColumnRangeType;
  $displayType?: string;
}

const maxColumns = 12;

const columnStyle = ($breakpoint: Breakpoint, $columns: ColumnRangeType) => css`
  @media ${({ theme }) => media(theme).greaterThan($breakpoint)} {
    flex: 0 0 ${(100 / maxColumns) * $columns}%;
    max-width: ${(100 / maxColumns) * $columns}%;
  }
`;

const S = {
  Container: styled.div``,

  Row: styled.div<StyledRowProps>`
    display: flex;
    flex: 1 1 auto;
    align-items: ${({ $align }) => $align};
    justify-content: ${({ $justify }) => $justify};
    flex-flow: ${({ $direction }) => $direction} wrap;
    min-height: 1px;

    &.cms-pdp-main-section-row {
      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        flex-wrap: nowrap;
        position: relative;
      }
    }
  `,

  Column: styled.div<StyledColumnProps>`
    flex: 0 0 100%;
    max-width: 100%;
    ${({ $displayType }) => css`
      display: ${$displayType || 'block'};
      pointer-events: ${$displayType === 'flex' ? 'none' : 'initial'};
      ${$displayType === 'flex' ? 'flex-direction: column;' : ''}
      ${$displayType === 'flex' ? 'align-items: flex-end;' : ''}
    `}

    &.cms-product-gallery-wrapper,
    &.cms-product-details-wrapper {
      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        flex: 1 1 auto;
        position: static;

        /* Override Layout style for Scrolled Type gallery */
        margin-top: 0;
      }
    }

    &.cms-product-details-wrapper {
      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        flex: 1 1 auto;
        position: sticky;
        min-height: auto;
        top: var(--top-nav-height);
        bottom: 0;
      }
    }

    @media ${({ theme }) => media(theme).lessThan('lg')} {
      display: block;
      pointer-events: initial;
      position: relative;
      top: initial;
      margin-top: initial;
    }

    ${({ $xs }) => $xs && columnStyle('xs', $xs)};
    ${({ $sm }) => $sm && columnStyle('sm', $sm)};
    ${({ $md }) => $md && columnStyle('md', $md)};
    ${({ $lg }) => $lg && columnStyle('lg', $lg)};
    ${({ $xl }) => $xl && columnStyle('xl', $xl)};

    &.cms-product-gallery-wrapper {
      width: 100vw;
      position: sticky;
      overflow: hidden;

      &[data-gallery-zoom-in='true'] {
        z-index: 1;
        height: calc(100vh - var(--top-nav-height)) !important;
        flex: 1 0 auto;
      }

      &[data-gallery-zoom-in='false'] {
        z-index: 0;
      }
    }
  `,
};

export const Container = ({
  children,
  className = '',
}: ContainerProps & PropsWithChildren): ReactElement => (
  <S.Container className={className}>{children}</S.Container>
);

type ComponentRowProps = RowProps & PropsWithChildren;

export const Row = ({
  children,
  justify = 'flex-start',
  align = 'flex-start',
  direction = 'row',
  className = '',
}: ComponentRowProps): ReactElement => (
  <S.Row $justify={justify} $align={align} $direction={direction} className={className}>
    {children}
  </S.Row>
);

export const Column = ({
  children,
  xs,
  sm,
  md,
  lg,
  xl,
  displayType,
  className = '',
}: ColumnProps & PropsWithChildren): ReactElement => {
  const { galleryZoomIn } = useGalleryContext();

  return (
    <S.Column
      className={className}
      $xs={xs}
      $sm={sm}
      $md={md}
      $lg={lg}
      $xl={xl}
      $displayType={displayType}
      data-gallery-zoom-in={galleryZoomIn}
    >
      {children}
    </S.Column>
  );
};
